<script setup lang="ts">
import { ref, computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { usersApi, type User } from '@/api/users';
import { usePermissions } from '@/composables/usePermissions';
import { useTableFilter } from '@/composables/useTableFilter';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ListToolbar from '@/components/list-toolbar.vue';
import UserForm from './user-form.vue';

const { data: users, isPending } = useQuery({
  queryKey: ['users'],
  queryFn: () => usersApi.index(),
});

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();

const usersHeaders = computed(() => {
  const headers = [
    { title: 'Email', key: 'email', sortable: true },
    { title: 'Nombre', key: 'fullName', sortable: true },
    { title: 'Rol', key: 'role.readableName', sortable: true },
    { title: 'Estado', key: 'isActive', sortable: true },
  ];

  if (hasPermission.value('users', 'update')) {
    headers.push({ title: 'Acciones', key: 'actions', sortable: false });
  }

  return headers;
});

const scope = ref('active');
const search = ref('');

const filteredUsers = computed(() => {
  if (!users.value) return [];

  const inScope = users.value.filter(u => (scope.value === 'active' ? u.isActive : !u.isActive));

  return inScope.map(u => ({ ...u, fullName: [u.firstName, u.lastName].filter(Boolean).join(' ') }));
});

const multiWordAccentInsensitiveFilter = useTableFilter(['email', 'fullName', 'role.readableName']);

const showFormDialog = ref(false);
const selectedUser = ref<User | null>(null);
const showSuccessSnackbar = ref(false);
const successMessage = ref('');

function openCreateDialog() {
  selectedUser.value = null;
  showFormDialog.value = true;
}

function openEditDialog(user: User) {
  selectedUser.value = user;
  showFormDialog.value = true;
}

function closeFormDialog() {
  showFormDialog.value = false;
  selectedUser.value = null;
}

function onFormSuccess() {
  const message = selectedUser.value ?
    'Cambios guardados exitosamente!' :
    'Usuario creado exitosamente. Se le enviará un email para establecer su contraseña.';
  successMessage.value = message;
  showSuccessSnackbar.value = true;
}
</script>
<template>
  <v-card class="w-100">
    <v-data-table
      v-bind="tableProps"
      :headers="usersHeaders"
      :items="filteredUsers"
      :loading="isPending"
      :search="search"
      :custom-filter="multiWordAccentInsensitiveFilter"
      :header-props="{ class: 'font-weight-bold' }"
      no-data-text="No se encontraron usuarios"
      loading-text="Cargando usuarios..."
    >
      <template #top>
        <list-toolbar
          v-model:search="search"
          title="Usuarios"
          search-label="Buscar"
        >
          <template #actions>
            <v-btn
              v-if="hasPermission('users', 'create')"
              class="text-white border bg-primary"
              @click="openCreateDialog"
            >
              <v-icon
                start
                icon="mdi-plus"
              />
              Agregar
            </v-btn>
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab value="active">
                Activos
              </v-tab>
              <v-tab value="inactive">
                Inactivos
              </v-tab>
            </v-tabs>
          </template>
        </list-toolbar>
      </template>
      <template #[`item.role.readableName`]="{ item }">
        {{ item.role?.readableName || item.role?.name }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          icon="mdi-pencil"
          size="small"
          variant="plain"
          @click="openEditDialog(item)"
        />
      </template>
      <template #[`item.isActive`]="{ item }">
        <v-chip
          :color="item.isActive ? 'success' : 'error'"
          size="small"
          variant="outlined"
        >
          {{ item.isActive ? 'Activo' : 'Inactivo' }}
        </v-chip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showFormDialog"
      max-width="500px"
      scrollable
    >
      <user-form
        :user="selectedUser || undefined"
        @close="closeFormDialog"
        @success="onFormSuccess"
      />
    </v-dialog>

    <v-snackbar
      v-model="showSuccessSnackbar"
      color="success"
      rounded="pill"
      :timeout="2000"
    >
      <v-icon
        icon="mdi-check-circle"
        class="mr-4"
      />
      {{ successMessage }}
    </v-snackbar>
  </v-card>
</template>
